import axiosInstance from '@/setup/axios/axiosInstance';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

import { InventoryReceiptFormData, InventoryReceiptRow, ReceiptLine } from '../types';

interface ApiReceiptLine {
  id?: string;
  sort_order?: number;
  item_id?: string;
  item_code?: string;
  item_name?: string;
  uom_id?: string;
  uom_name?: string;
  received_qty?: string | number;
  accepted_qty?: string | number;
  rejected_qty?: string | number;
  unit_cost?: string | number;
  line_amount?: string | number;
  purchase_line_id?: string | null;
  lot_no?: string;
  serial_numbers?: string;
  note?: string;
}

interface ApiReceipt {
  id: string;
  receipt_no?: string;
  receipt_type?: string;
  receipt_type_display?: string;
  status?: string | { id?: string; code?: string; name?: string; color?: string; is_posted?: boolean };
  status_code?: string;
  status_display?: string;
  status_color?: string;
  is_posted?: boolean;
  company_id?: string;
  company_name?: string;
  supplier_id?: string;
  supplier_name?: string;
  warehouse_id?: string;
  warehouse_name?: string;
  employee_id?: string;
  employee_name?: string;
  purchase_id?: string | null;
  purchase_no?: string | null;
  received_at?: string;
  posted_at?: string | null;
  posted_by?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  created_at?: string | null;
  updated_by?: string | null;
  updated_by_name?: string | null;
  updated_at?: string | null;
  note?: string;
  lines?: ApiReceiptLine[];
  [key: string]: any;
}

interface ApiListResponse {
  results?: ApiReceipt[];
  count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  has_next?: boolean;
  has_previous?: boolean;
  next?: string | null;
  previous?: string | null;
}

export interface GetReceiptsResponse {
  results: InventoryReceiptRow[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface GetReceiptsParams {
  companyId?: string;
  supplierId?: string;
  itemId?: string;
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
const normalizeApiDecimal = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '0';
  }

  const rawValue = String(value).trim().replace(/\s/g, '');
  if (!rawValue) {
    return '0';
  }

  const lastComma = rawValue.lastIndexOf(',');
  const lastDot = rawValue.lastIndexOf('.');
  let normalizedValue = rawValue;

  if (lastComma >= 0 && lastDot >= 0) {
    normalizedValue = lastComma > lastDot
      ? rawValue.replace(/\./g, '').replace(',', '.')
      : rawValue.replace(/,/g, '');
  } else if (lastComma >= 0) {
    normalizedValue = rawValue.replace(',', '.');
  }

  return Number.isFinite(Number(normalizedValue)) ? normalizedValue : '0';
};

const mapApiLine = (line: ApiReceiptLine, index: number): ReceiptLine => ({
  id: line.id || `line-${index}`,
  sort_order: line.sort_order ?? index + 1,
  item_id: line.item_id || '',
  item_code: line.item_code || '',
  item_name: line.item_name || '',
  uom_id: line.uom_id || '',
  uom_name: line.uom_name || '',
  received_qty: normalizeApiDecimal(line.received_qty),
  accepted_qty: normalizeApiDecimal(line.accepted_qty),
  rejected_qty: normalizeApiDecimal(line.rejected_qty),
  unit_cost: normalizeApiDecimal(line.unit_cost),
  line_amount: normalizeApiDecimal(line.line_amount),
  purchase_line_id: line.purchase_line_id || null,
  lot_no: line.lot_no || '',
  serial_numbers: line.serial_numbers || '',
  note: line.note || '',
});

const resolveStatusId = (status: ApiReceipt['status']) => {
  if (typeof status === 'object' && status !== null) {
    return status.id || status.code || '';
  }

  return status || '';
};

const resolveStatusCode = (receipt: ApiReceipt) => {
  if (receipt.status_code) {
    return receipt.status_code;
  }

  if (typeof receipt.status === 'object' && receipt.status !== null) {
    return receipt.status.code || '';
  }

  return '';
};

const resolveStatusDisplay = (receipt: ApiReceipt) => {
  if (receipt.status_display) {
    return receipt.status_display;
  }

  if (typeof receipt.status === 'object' && receipt.status !== null) {
    return receipt.status.name || '';
  }

  return '';
};

const resolveStatusColor = (receipt: ApiReceipt) => {
  if (receipt.status_color) {
    return receipt.status_color;
  }

  if (typeof receipt.status === 'object' && receipt.status !== null) {
    return receipt.status.color || '';
  }

  return '';
};

const resolveIsPosted = (receipt: ApiReceipt): boolean => (
  receipt.is_posted === true
  || (
    typeof receipt.status === 'object'
    && receipt.status !== null
    && receipt.status.is_posted === true
  )
);

const mapApiToRow = (r: ApiReceipt): InventoryReceiptRow => ({
  id: r.id,
  receipt_no: r.receipt_no || '',
  receipt_type: r.receipt_type || 'direct',
  receipt_type_display: r.receipt_type_display || r.receipt_type || '',
  status: resolveStatusId(r.status),
  status_code: resolveStatusCode(r),
  status_display: resolveStatusDisplay(r),
  status_color: resolveStatusColor(r),
  is_posted: resolveIsPosted(r),
  supplier_id: r.supplier_id || '',
  supplier_name: r.supplier_name || '',
  warehouse_id: r.warehouse_id || '',
  warehouse_name: r.warehouse_name || '',
  employee_id: r.employee_id || '',
  employee_name: r.employee_name || '',
  purchase_id: r.purchase_id || null,
  purchase_no: r.purchase_no || null,
  received_at: r.received_at || '',
  company_id: r.company_id || '',
  company_name: r.company_name || '',
  created_by: r.created_by || null,
  created_by_name: r.created_by_name || '',
  created_at: r.created_at || null,
  updated_by: r.updated_by || null,
  updated_by_name: r.updated_by_name || '',
  updated_at: r.updated_at || null,
  note: r.note || '',
  lines: (r.lines || []).map(mapApiLine),
});

const mapApiToFormData = (r: ApiReceipt): InventoryReceiptFormData => ({
  receipt_no: r.receipt_no || '',
  receipt_type: r.receipt_type || 'direct',
  status: resolveStatusId(r.status),
  status_code: resolveStatusCode(r),
  status_display: resolveStatusDisplay(r),
  status_color: resolveStatusColor(r),
  is_posted: resolveIsPosted(r),
  company_id: r.company_id || '',
  company_name: r.company_name || '',
  supplier_id: r.supplier_id || '',
  supplier_name: r.supplier_name || '',
  warehouse_id: r.warehouse_id || '',
  warehouse_name: r.warehouse_name || '',
  employee_id: r.employee_id || '',
  employee_name: r.employee_name || '',
  purchase_id: r.purchase_id || null,
  purchase_no: r.purchase_no || null,
  return_source_issue_id: null,
  return_source_issue_no: null,
  received_at: r.received_at || new Date().toISOString(),
  posted_at: r.posted_at || null,
  posted_by: r.posted_by || null,
  note: r.note || '',
  lines: (r.lines || []).map(mapApiLine),
});

const resolveReceiptsCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'global' || resolved === 'all' || !isUuid(resolved)) {
    return undefined;
  }
  return resolved;
};

export async function getReceipts(params: GetReceiptsParams = {}): Promise<GetReceiptsResponse> {
  const query = new URLSearchParams();
  const companyId = resolveReceiptsCompanyId(params.companyId);
  if (companyId) query.append('company', companyId);
  if (params.supplierId) query.append('supplier', params.supplierId);
  if (params.itemId) query.append('item', params.itemId);
  if (params.search) query.append('search', params.search);
  if (params.status && params.status !== 'all') query.append('status', params.status);
  if (params.page) query.append('page', String(params.page));
  if (params.pageSize && params.pageSize > 0) query.append('page_size', String(params.pageSize));

  const qs = query.toString();
  const response = await axiosInstance.get<ApiListResponse | ApiReceipt[]>(`/inventory/receipts/${qs ? `?${qs}` : ''}`);
  const data = response.data;

  if (Array.isArray(data)) {
    return {
      results: data.map(mapApiToRow),
      count: data.length,
      page: 1,
      pageSize: data.length,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
    };
  }

  return {
    results: (data.results || []).map(mapApiToRow),
    count: data.count || 0,
    page: data.page || params.page || 1,
    pageSize: data.page_size || params.pageSize || 0,
    totalPages: data.total_pages || 1,
    hasNext: Boolean(data.has_next),
    hasPrevious: Boolean(data.has_previous),
  };
}

export async function getReceiptDetail(id: string): Promise<InventoryReceiptFormData> {
  const response = await axiosInstance.get<ApiReceipt>(`/inventory/receipts/${id}/`);
  return mapApiToFormData(response.data);
}

export async function saveReceipt(form: InventoryReceiptFormData, id?: string): Promise<ApiReceipt> {
  const normalizeStatus = (status: InventoryReceiptFormData['status']): string | null => {
    if (typeof status === 'object' && status !== null) {
      return status.id || status.code || null;
    }

    return status || null;
  };

  const normalizeReceiptType = (value?: string, purchaseId?: string | null, returnSourceIssueId?: string | null): string => {
    const normalized = String(value || '').trim().toLowerCase();

    if (normalized === 'from_purchase' || normalized === 'direct' || normalized === 'return') {
      return normalized;
    }

    if (purchaseId) {
      return 'from_purchase';
    }

    if (returnSourceIssueId) {
      return 'return';
    }

    return 'direct';
  };

  const normalizeQty = (value: string | number | null | undefined): number => {
    const num = Number(normalizeApiDecimal(value));
    return Number.isFinite(num) && num > 0 ? num : 0;
  };

  const normalizeAmount = (value: string | number | null | undefined): number => {
    const num = Number(normalizeApiDecimal(value));
    return Number.isFinite(num) && num >= 0 ? num : 0;
  };

  const selectedPurchaseId = form.purchase_id || null;
  const payload: any = {
    receipt_no: form.receipt_no || undefined,
    receipt_type: normalizeReceiptType(form.receipt_type, form.purchase_id, form.return_source_issue_id || null),
    status: normalizeStatus(form.status),
    company: form.company_id,
    supplier_id: form.supplier_id || null,
    warehouse_id: form.warehouse_id || null,
    employee_id: form.employee_id || null,
    purchase_id: selectedPurchaseId,
    received_at: form.received_at || null,
    note: form.note || '',
    lines: (form.lines || []).map((line) => ({
      id: line.id,
      sort_order: line.sort_order,
      item_id: line.item_id || null,
      uom_id: line.uom_id || null,
      received_qty: normalizeQty(line.received_qty),
      accepted_qty: normalizeQty(line.received_qty),
      rejected_qty: 0,
      unit_cost: normalizeAmount(line.unit_cost),
      purchase_line_id: line.purchase_line_id || null,
      lot_no: line.lot_no || '',
      serial_numbers: line.serial_numbers || '',
      note: line.note || '',
    })),
  };

  if (id) {
    const response = await axiosInstance.put<ApiReceipt>(`/inventory/receipts/${id}/`, payload);
    return response.data;
  }

  // For create flow, avoid backend create_from_purchase branch so edited lines
  // (e.g. deleted PO items) are preserved exactly as user configured.
  const createPayload = {
    ...payload,
    purchase_id: null,
  };

  const response = await axiosInstance.post<ApiReceipt>('/inventory/receipts/', createPayload);

  if (selectedPurchaseId && response.data?.id) {
    const attachResponse = await axiosInstance.put<ApiReceipt>(`/inventory/receipts/${response.data.id}/`, payload);
    return attachResponse.data;
  }

  return response.data;
}

export async function createReceiptFromPurchase(purchaseId: string, companyId?: string, warehouseId?: string): Promise<ApiReceipt> {
  const payload: any = {
    po_id: purchaseId,
    purchase_id: purchaseId,
  };

  if (companyId) {
    payload.company_id = companyId;
  }
  if (warehouseId) {
    payload.warehouse_id = warehouseId;
  }

  const response = await axiosInstance.post<ApiReceipt>('/inventory/receipts/', payload);
  return response.data;
}

export async function postReceipt(id: string): Promise<ApiReceipt> {
  const response = await axiosInstance.post<ApiReceipt>(`/inventory/receipts/${id}/post/`);
  return response.data;
}
